import React, { FC, useState, useEffect, useContext } from "react";
import {
    Table, Icon, Dimmer, Loader, Grid, Input, Button,
    Popup, SemanticWIDTHS, SemanticICONS, Dropdown, Message
} from "semantic-ui-react";
import { toast } from 'react-toastify';
import axios, { AxiosResponse } from "axios";
import { requestSort, SORT_ICON, SORT_ORDER } from "../../services/SortService";
import ResultItem from "./ResultItem";
import PaginationNav from "../Results/PaginationNav";
import { IGridDisplay, IGridField, IRecord, ITableActions } from "../Interfaces/TableInterface";
import AuthContext from "../../contexts/AuthContext";
import { postRequest } from "../../services/axiosService";
import Header from "../Layout/iFrameLayout/Header";
import { IDropItem } from "../Interfaces/AdminInterface";
import DefaultActionDisplay from "./DefaultActionDisplay";
import { getSubStringValue } from "../../utils/stringUtils";

const GridDisplay: FC<any> = () => {
    let searchVal = window.location.search;
    let params = new URLSearchParams(searchVal);
    let tableId: string = params.get('id')!;
    let tableQuery: string = params.get('query')!;
    let tableSort: string = params.get('sort')!;
    let tablePageSize: string = params.get('pageSize')!;
    let tableOptions: string = params.get('options')!;
    let tableTheme: string = params.get('theme')!;
    let tableFields: string = params.get('fields')!;
    let tableName: string = params.get('name')!;
    let tableDescription: string = params.get('description')!;
    let tableTemplate: string = params.get('template')!;

    const { auth } = useContext(AuthContext);

    const [id, setId] = useState<string>(tableId);
    const [grid, setGrid] = useState<IGridDisplay>();
    const [title, setTitle] = useState<string>("");
    const [shortTitle, setShortTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [shortDescription, setShortDescription] = useState<string>("");
    const [theme, setTheme] = useState<any>();
    const [viewDisplay, setViewDisplay] = useState<boolean>(false);
    const [editDisplay, setEditDisplay] = useState<boolean>(false);
    const [displayLogo, setDisplayLogo] = useState<boolean>(false);
    const [downloadDisplay, setDownloadDisplay] = useState<boolean>(false); 
    const [renditionDisplay, setRenditionDisplay] = useState<boolean>(false);
    const [viewRenditionDisplay, setViewRenditionDisplay] = useState<boolean>(false);
    const [displayFileIcon, setDisplayFileIcon] = useState<boolean>(false);
    const [displayRecIcon, setDisplayRecIcon] = useState<boolean>(false);
    const [displayWordWrap, setDisplayWordWrap] = useState<boolean>(false);
    const [records, setRecords] = useState<any[]>([]);
    const [fields, setFields] = useState<IGridField[]>([]);
    const [customSizing, setCustomSizing] = useState<boolean>(false);
    const [disableSearch, setDisableSearch] = useState<boolean>(false);
    const [disableTitle, setDisableTitle] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [recordsPerPage, setRecordsPerPage] = useState<number>(25);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [activePage, setActivePage] = useState<number>(1);
    const [timeLastCached, setTimeLastCached] = useState<string>("");
    const [defaultAction, setDefaultAction] = useState<ITableActions>(ITableActions.None);
   

    const [isCacheTable, setIsCacheTable] = useState<boolean>(false);
    const [allRecords, setAllRecords] = useState<any[]>([]);
    const [filteredRecords, setAllFilteredRecords] = useState<any[]>([]);


    const [loading, setLoading] = useState<boolean>(false);
    const [currentSortName, setCurrentSortName] = useState<string>("");
    const [sortIcon, setSortIcon] = useState<SemanticICONS>(SORT_ICON.ASC);
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.ASC);
    const [search, setSearch] = useState<string>("");
    const [fieldSelected, setFieldSelected] = useState<string>("");
    const [fieldOptions, setFieldOptions] = useState<IDropItem[]>([]);  

    const [columnWidth, setColumnWidth] = useState<SemanticWIDTHS>(1);

    useEffect(() => {
        async function getData() {
            await getResults("", 1);
            setLoading(false);
        }
        getData();
    }, []);


    const extractVParams = (url:string) => {
        const regex = /v\d+=([^&]+)/g;
        let match;
        const params = [];

        while ((match = regex.exec(url)) !== null) {
            // Decode the parameter value (e.g., 'Stacy%20Mom' becomes 'Stacy Mom')
            const decodedValue = decodeURIComponent(match[1]);
            params.push(decodedValue);
        }

        return params;
    };

    const filterCacheTable = (query: string, field: string) => {
        if (query === "") {
            //if query is empty, return all records
            setRecords(allRecords);
        }
        else {
            //filter all records by field and query
            let filteredRecords = allRecords.filter((record: any) => {
                if (field.toLowerCase() === "default") {
                    //search across all fields
                    return record.tableFields.some((x: any) =>
                        x.recordValue &&
                        x.recordValue.toString().toLowerCase().includes(query.toLowerCase())
                    );
                }
                else {
                    //search across selected field
                    let fieldValue = record.tableFields.find((x: any) => x.fieldName.toLowerCase() === field.toLowerCase());
                    if (fieldValue) {
                        return fieldValue.recordValue.toString().toLowerCase().includes(query.toLowerCase());
                    }
                }
                return false;
            });
            setRecords(filteredRecords);
            setAllFilteredRecords(filteredRecords); 

            //update page results
            getCachedPageResults(filteredRecords, 1, recordsPerPage);

            //calculate total pages 
            let pages = Math.ceil(filteredRecords.length / recordsPerPage);
            setTotalPages(pages);

            //set total results
            setTotalRecords(filteredRecords.length);

        }

    }

    const getResults = async (query: string, page: number, refreshCache : boolean = false) => {
        let placeholders: string[] = [];
        let searchQuery = "";

        let decodeQuery = decodeURIComponent(window.location.search);

        //Temp table created from query passed 
        if (!decodeQuery.toLocaleLowerCase().includes("?id=")) {

            //Identify placeholders for Dynamic Search 
            placeholders = extractVParams(decodeQuery);
            if (placeholders.length > 0) {

                tableQuery = ""; //leave query blank as query is obtained from the table id 
            }
            else
            {
                //Standard CM2WebShare Query Table
                if (decodeQuery.toLocaleLowerCase().includes("?query="))
                {
                    //get query from search
                    searchQuery = decodeQuery.split('&')[0].replace('?query=', '');
                    tableQuery = searchQuery;
                }
                else
                {
                    //Legacy link where query is passed through without prefix ?query=
                    let searchingValues = decodeQuery.split('&')[0].split('?').filter(x => x !== "");
                    searchQuery = searchingValues[0];
                    tableOptions = searchingValues[1].replace("options=", "");
                    tableQuery = searchQuery;
                }
            }
        }
        try {
            setLoading(true);
            
            if (query === "") {
                setSearch("");
                setFieldSelected("");
            }

            //filter by field
            if (query !== "" && fieldSelected !== "" && isCacheTable) {
                let fieldVal = fieldOptions.filter(x => x.value.toLowerCase() === fieldSelected.toLowerCase());
                filterCacheTable(query, fieldVal[0].text);
                setLoading(false);
                return;
            }


            let settings = {
                id: id,
                searchQuery: query,
                filterBy: fieldSelected,
                dynamicPlacholders:placeholders,
                page: page,
                tableQuery: tableQuery,
                sort: tableSort,
                pageSize: tablePageSize,
                options: tableOptions,
                theme: tableTheme,
                fields: tableFields,
                name: tableName,
                description: tableDescription,
                template: tableTemplate,
                refreshCache: refreshCache
            }

            let response: AxiosResponse = await postRequest(auth, `/Grid/GridResults`, settings);

            if (response.data) {
                let data: IGridDisplay = response.data;
                setGrid(data);
                setId(data.id);
                setTitle(data.title);
                setShortTitle(data.shortTitle);
                setDescription(data.description);
                setShortDescription(data.shortDescription);
                setTheme(data.theme);
                setDisplayLogo(data.displayLogo);
                setDisableSearch(data.disableSearchBar);
                setDisableTitle(data.disableTitle);
                setDownloadDisplay(data.downloadDocument);
                setViewDisplay(data.viewDocDisplay);
                setEditDisplay(data.editDocDisplay);
                setRenditionDisplay(data.renditionDisplay);
                setViewRenditionDisplay(data.viewRenditionDisplay);
                setDisplayFileIcon(data.displayFileIcon);
                setDisplayRecIcon(data.displayRecTypeIcon);
                setDisplayWordWrap(data.wordWrap);
                setFields(data.fields);

                if (data.fields.length > 0) {
                    getFieldOptions(data.fields);
                    setFieldSelected(fieldSelected === "" ? data.fields[0].searchClauseId: fieldSelected);
                }
              
                setCustomSizing(data.customSizing);
                setTotalRecords(data.totalRecords);
                setTotalPages(data.totalPages);
                setRecordsPerPage(data.recordsPerPage);
                setTimeLastCached(data.timeLastCached);

                //define cached table
                if (data.timeLastCached !== null && data.timeLastCached !== "") {
                    //Cached data will return all data for the entire table, need to do pagination manually
                    setIsCacheTable(true);
                    setAllRecords(data.records);

                    //paginate to get the first tables data
                    getCachedPageResults(data.records, 1, data.recordsPerPage);
                }
                else {
                    //standard tables will return data page by page 
                    setRecords(data.records);
                }

                //get default record action 
                setDefaultAction(data.records.length > 0 ? data.records[0].defaultAction: ITableActions.None);

                if (!data.customSizing) {
                    //calculate column width based on number of fields
                    calcColumnWidth(data.fields);
                }

                //Update pagination 
                setActivePage(page);
               
            }
            setLoading(false);
        } catch (error) {
            console.log(error);
            toast.error("Error retrieving results");
            setLoading(false);
        }
    }

    const getFieldOptions = (fields: IGridField[]) => {

        let options: IDropItem[] = [];

        let defaultOption: IDropItem = {
            key: "default",
            value: "default",
            name: "Default",
            text: "Default",
            image: ""
        }
        options.push(defaultOption);

        for (var i = 0; i < fields.length; i++) {
            let field : IGridField = fields[i];
            let option: IDropItem = {
                key: field.propertyId,
                value: field.searchClauseId,
                name: field.name,
                text: (field.alias === null || field.alias === "") ? field.name : field.alias,
                image: ""
            }
            options.push(option);
        }
        setFieldOptions(options);
    }

    const getMinWith = () => {
        if (grid && grid.fields)
        {
            if (grid.fields.length <= 2)
                return "50%";
            else if (grid.fields.length >= 3 || grid.fields.length <= 5)
                return "70%";
            else
                return "100%"
        }
    }

    const onKeyPressed = (event: any) => {
        if (event.key === 'Enter') {
            getResults(search, 1);
        }
    }

    const getCachedPageResults = (data: any[], currentPage: number, recPerPage: number) => {
        let paginatedResults = data.slice((currentPage - 1) * recPerPage, currentPage * recPerPage);
        setRecords(paginatedResults);
    }

    const paginateResults = (currentPage: number, recPerPage: number) => {
        if (isCacheTable) {

            let recordsToUse = allRecords;

            if (search !== "" && fieldSelected !== "") {
                //filter records based on search and field selected
                recordsToUse = filteredRecords;
            }

            getCachedPageResults(recordsToUse, currentPage, recPerPage);
        }
        else {
            getResults(search, currentPage);
        } 
    }

    const handlePaginationChange = (activePage: number) => {
        try {
            setActivePage(activePage);
            paginateResults(activePage, recordsPerPage);
            // scroll to top of page
            let mainDiv: any = document.getElementById('site-content');
            mainDiv.scrollTop = 0;

        } catch (e) {

        }
      
    }

    const onFieldChange = (e:any, data:any) => {
        setFieldSelected(data.value);
    }

    const updateCount = (count: number, toAdd: boolean) => {
        let currentCount = totalRecords;
        if (toAdd) {
            currentCount = currentCount + count;
        } else {
            currentCount = currentCount - count;
        }

        setTotalRecords(currentCount);
    }

    const calcColumnWidth = (fields: IGridField[]) =>
    {
        //check to see if the record title is part of the fields
        let hasNumber = fields.some((field) => field.propertyId.toLowerCase().includes("recordnumber"));

        let calcultedWidth = 0;

        let fieldCount = hasNumber ? fields.length - 1 : fields.length;

        let width =  hasNumber? 10 : 12; //16 - 2 for 2 columns that dont change / or take an extra 2 for record number column

        if (fieldCount > 0) {
            calcultedWidth = width / fieldCount;
        }

        let widthS = calcultedWidth.toFixed() as SemanticWIDTHS;
        setColumnWidth(widthS);
    }

    if (loading) {
        return (
            <Dimmer inverted active={true}>
                <Loader inverted>Loading Results...</Loader>
            </Dimmer>
        )
    }

    return (
        <>

        { displayLogo && (
            <Header />
        )}

        <div id="search-results" className="slide-in-right" style={{ marginTop: "30px", marginBottom: "20px", paddingBottom: "40px", width: "90%", marginRight: "auto", marginLeft: "auto", paddingTop: "20px", maxHeight: "800px", minHeight: "800px", height: "800px" }}>

            {!disableTitle && (
                <>
                        {shortTitle != null ? <Popup wide='very' content={title} trigger={<div className="header blue headerStyleBlue" style={{ marginLeft: 0 }}>{shortTitle}</div>} /> : <div className="header blue headerStyleBlue" style={{ marginLeft: 0 }}>{title}</div>}
                    {shortDescription != null ? <Popup wide='very' content={description} trigger={<div className="meta" style={{ marginBottom: 10 }}>{shortDescription}</div>} /> : <div className="meta" style={{ marginBottom: 10 }}>{description}</div>}
                </>
            )}
            <Grid>
                <Grid.Column width={16} style={{ height: "800px" }}>
                    <Grid stackable style={{
                        marginBottom: "15px"
                    }} columns="equal">
                    {!disableSearch && (
                        <Grid.Column position="left" className="fluid">
                            <div className="header gray headerStyleGray" style={{ marginLeft: '0px' }}>FILTER BY</div>
                                    <Dropdown id="field-dropdown" placeholder='Select Field' style={{ backgroundColor: "#e5e5e5", width: "15%", float: "left", }} fluid selection options={fieldOptions} onChange={onFieldChange} value={fieldSelected} />

                            <div onKeyDown={onKeyPressed} style={{ width: "100%" }}>
                                <Input id="results-search-name" className="bgGrey" style={{ width: "40%", float: "left", marginLeft:10}} fluid
                                        placeholder={'Search Records...'}
                                        value={search}
                                    onChange={(e) => { setSearch(e.target.value) }} />

                                <Button id="results-search-btn" style={{ overflow: "hidden", float: "left", marginLeft:10 }}
                                        className="buttton" onClick={(e) => { e.preventDefault(); getResults(search, 1) }}>Search</Button>
                                <Button id="results-search-btn" style={{ overflow: "hidden", float: "left"}}
                                    className="buttton" onClick={(e) => { e.preventDefault();  getResults("", 1) }}>Clear</Button>
                            </div>
                        </Grid.Column>
                
                    )}       
                </Grid>
                <div>
                    <div className="meta">Total Results: {totalRecords}</div>

                    {defaultAction !== ITableActions.None && (
                        <div style={{ justifySelf: 'center'}}>
                            <DefaultActionDisplay defaultAction={defaultAction} />
                        </div>
                    )}
                </div>
                        <Table collapsing fixed style={{ minWidth: getMinWith() }} id="search-results-table">
                        <Table.Header id="tableHeader">
                                <Table.Row>
                                <Table.HeaderCell width={1}>
                                </Table.HeaderCell>
                                    {fields.map((f: IGridField) => {
                                        let name = (f.alias === null || f.alias === "") ? f.name : f.alias
                                        let headerValue = name === "" ? "" : getSubStringValue(name);

                                        if (f.propertyId.toLowerCase().includes("recordnumber")) {
                                            return (
                                                <Table.HeaderCell key={f.propertyId} width={customSizing ? f.colWidth as SemanticWIDTHS : 2}
                                                    onClick={() => requestSort(f.name, f.objectType, "tableFields", setCurrentSortName, setSortIcon, setSortOrder, sortOrder, setRecords, records, true)}>
                                                    {headerValue.includes("...") ? <Popup content={name} trigger={<span>{headerValue}</span>} /> : headerValue}{currentSortName === f.name ? <Icon name={sortIcon} /> : <Icon name={SORT_ICON.DEFAULT} />}
                                                </Table.HeaderCell>
                                            )
                                        }
                                        else
                                        {
                                            return (
                                                <Table.HeaderCell key={f.propertyId} width={customSizing ? f.colWidth as SemanticWIDTHS : columnWidth}
                                                    onClick={() => requestSort(f.name, f.objectType, "tableFields", setCurrentSortName, setSortIcon, setSortOrder, sortOrder, setRecords, records, true)}>
                                                    {headerValue.includes("...") ? <Popup content={name} trigger={<span>{headerValue}</span>} /> : headerValue}{currentSortName === f.name ? <Icon name={sortIcon} /> : <Icon name={SORT_ICON.DEFAULT} />}
                                                </Table.HeaderCell>
                                            ) 
                                        }
                                })}
                            <Table.HeaderCell width={3}>
                            </Table.HeaderCell>
                        </Table.Row>
                        </Table.Header>
                        <Table.Body id="records-table">
                                {records.map((x: IRecord) => {
                                return <ResultItem key={`${x.uri}-rec`} record={x} viewDisplay={viewDisplay} editDisplay={editDisplay}
                                    renditionDisplay={renditionDisplay} viewRenditionDisplay={viewRenditionDisplay} downloadDisplay={downloadDisplay}
                                    displayFileIcon={displayFileIcon} displayRecIcon={displayRecIcon} gridId={id} addPadding=""
                                    onChildChange={updateCount} wordWrap={displayWordWrap}                                   />
                            })}
                        </Table.Body>
                    </Table>
                    <div className="resultsNav" style={{ marginTop: "20px", paddingBottom: "20px" }}>
                        <PaginationNav totalPages={totalPages} onChange={handlePaginationChange} activePage={activePage} />

                        {isCacheTable && (
                            <div style={{ marginTop: 30, minWidth: getMinWith() }}>
                                <Message
                                    info
                                    icon='info circle'
                                    header='Cached Table Data'
                                    content={`This table was last cached at ${timeLastCached}`}
                                />
                            </div>
                        )}
                           
                    </div>
                </Grid.Column>
                </Grid>
            </div>
        </>
    )
}

export default GridDisplay;